import type { ComponentProps, ReactNode } from "react"
import { cn } from "@/lib/utils"

// The content column of a screen — the layout primitive every module page starts from.
//
// ANCHORED, not centered. The old `mx-auto max-w-*` idiom floated every page in the middle
// of the slab, which on wide monitors reads as a lost island in a sea of margin. A screen's
// content column instead starts where the site header's title starts and takes a purposeful
// width for its archetype; the leftover space stays on the trailing side, where it reads as
// room, not as drift. (RTL mirrors automatically: "start" is a logical edge.)
//
// One width per archetype — pick the narrowest that fits the densest row on the screen:
//
//   form   forms of scalar fields (payment entry, cash entry, transfer)
//   wide   forms with an embedded line grid, hubs, trees (journal entry, chart of accounts)
//   board  side-by-side workbenches and wide tables (ledger, reconcile)
//   full   the rare screen that genuinely wants the whole slab
//
// Vertical rhythm between page regions (intro → notices → body) is 24px (`space-y-6`);
// tighter rhythms live inside the regions. Norn routes own the horizontal gutter directly
// (`px-4 md:px-6`); the original route slab's `p-4` is not present here.
const SCREEN_WIDTHS = {
  form: "max-w-3xl",
  wide: "max-w-4xl",
  board: "max-w-6xl",
  full: "max-w-none",
} as const

export type ScreenWidth = keyof typeof SCREEN_WIDTHS

export function Screen({
  width = "wide",
  className,
  children,
  ...props
}: {
  width?: ScreenWidth
  className?: string
  children: ReactNode
} & Omit<ComponentProps<"div">, "children" | "className">) {
  return (
    <div className={cn("w-full space-y-6", SCREEN_WIDTHS[width], className)} {...props}>
      {children}
    </div>
  )
}
